import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useMemo } from 'react';
import {
    R2FThresholdData,
    PendingThresholdChange,
    R2FProductionCapabilityData,
    R2FNodeData
} from 'src/features/types/r2fTypes';
import { DEFAULT_WHTS } from 'src/types/WarehouseHandlingTier';
import {
    getR2FThresholdsForWarehouse,
    publishR2FThresholds,
    deleteR2FThresholds
} from 'src/utils/Queries';
import { useIsDefaultFCPCEnabled } from 'src/core/hooks/useIsDefaultFCPCEnabled';
import {
    HandlingTierByPC,
    PublishedR2FThresholds,
    R2FToPublish,
    DeleteR2FThresholdsRequest,
} from 'src/features/types/gslaApi';

/**
 * Custom hook for managing R2F threshold data
 */
export function useR2FThresholds(
    warehouseId: string, 
    userAlias: string = '', 
    userCanEdit: boolean = false,
    preserveDefaultTypes: boolean = true
) {
    const queryClient = useQueryClient();
    
    // Ensure warehouseId is never empty
    const effectiveWarehouseId = warehouseId || 'DEFAULT';

    // Determine if Default_FC_PC should be shown (false for SSD sites)
    const { isDefaultFCPCEnabled } = useIsDefaultFCPCEnabled(warehouseId);

    // Fetch threshold data for the FC warehouse using the single-warehouse endpoint
    const { data: fcData, isLoading: isFcLoading, error: fcError } = useQuery(
        ['r2f-thresholds-warehouse', effectiveWarehouseId, preserveDefaultTypes],
        () => getR2FThresholdsForWarehouse(effectiveWarehouseId, preserveDefaultTypes),
        {
            enabled: true,
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
        }
    );

    // Fetch threshold data for the DEFAULT warehouse (contains Default_Network_PC)
    const { data: defaultData, isLoading: isDefaultLoading, error: defaultError } = useQuery(
        ['r2f-thresholds-warehouse', 'DEFAULT', preserveDefaultTypes],
        () => getR2FThresholdsForWarehouse('DEFAULT', preserveDefaultTypes),
        {
            enabled: true,
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
        }
    );

    const isLoading = isFcLoading || isDefaultLoading;
    const error = fcError || defaultError;

    // Merge FC and DEFAULT r2fThreshold maps — DEFAULT as base, FC overrides on top
    const apiData = useMemo(() => {
        // If FC data is undefined, nothing to show
        if (!fcData) {
            return undefined;
        }

        // If DEFAULT data is undefined, gracefully degrade to FC-only data
        if (!defaultData) {
            return fcData;
        }

        // Start with DEFAULT warehouse r2fThreshold as the base (contains Default_Network_PC)
        const merged: HandlingTierByPC = { ...defaultData.r2fThreshold };

        // Overlay FC-specific r2fThreshold — FC data takes precedence for overlapping PC keys
        Object.entries(fcData.r2fThreshold).forEach(([pcName, tiers]) => {
            if (merged[pcName]) {
                // For overlapping PCs, merge at the handling-tier level
                merged[pcName] = { ...merged[pcName], ...tiers };
            }
            else {
                merged[pcName] = tiers;
            }
        });

        // Filter out Default_FC_PC for SSD sites
        if (!isDefaultFCPCEnabled) {
            delete merged['Default_FC_PC'];
        }

        return { warehouseId: fcData.warehouseId, r2fThreshold: merged };
    }, [fcData, defaultData, isDefaultFCPCEnabled]);

    // Adapt API response to component format
    const adaptedData = useMemo<R2FThresholdData | undefined>(() => {
        if (!apiData) return undefined;
        return adaptApiResponse(apiData, effectiveWarehouseId);
    }, [apiData, effectiveWarehouseId]);

    // Mutation for saving thresholds
    const saveMutation = useMutation<void, Error, PendingThresholdChange[]>(
        async (changes: PendingThresholdChange[]) => {
            // Convert changes to API request format
            const r2fToPublishList = prepareThresholdPublishRequest(changes, effectiveWarehouseId, userAlias);
            
            // Call API
            const result = await publishR2FThresholds({ r2fToPublishList });
            if (!result) {
                throw new Error('Failed to publish R2F thresholds');
            }
            return;
        },
        {
            onSuccess: () => {
                // Invalidate both FC and DEFAULT queries to refresh data from both sources
                queryClient.invalidateQueries(['r2f-thresholds-warehouse', effectiveWarehouseId, preserveDefaultTypes]);
                queryClient.invalidateQueries(['r2f-thresholds-warehouse', 'DEFAULT', preserveDefaultTypes]);
            }
        }
    );

    // Save function to pass to the component
    const saveThresholds = async (changes: PendingThresholdChange[]) => {
        // Extract deletion changes to handle separately
        const deletionChanges = changes.filter(change => change.action === 'Delete');
        const nonDeletionChanges = changes.filter(change => change.action !== 'Delete');
        
        // Process deletion changes first
        for (const change of deletionChanges) {
            const isDeletingFCDefault = change.productionCapability === 'Default_FC_PC' && change.handlingTier === 'DEFAULT';
            
            const request: DeleteR2FThresholdsRequest = {
                warehouseId: effectiveWarehouseId,
                productionCapability: change.productionCapability,
                warehouseHandlingTier: change.handlingTier !== 'DEFAULT' ? change.handlingTier : undefined,
                userAlias: change.userAlias || userAlias,
                actionSource: change.actionSource || 'Newton Dashboard',
                reason: change.reason || 'UI Delete'
            };
            
            try {
                await deleteMutation.mutateAsync(request);
                
                // If we're deleting the FC default PC, force a refresh to ensure Network default is shown
                if (isDeletingFCDefault) {
                    queryClient.invalidateQueries(['r2f-thresholds-warehouse', effectiveWarehouseId, preserveDefaultTypes], {
                        refetchActive: true,
                        refetchInactive: true
                    });
                    queryClient.invalidateQueries(['r2f-thresholds-warehouse', 'DEFAULT', preserveDefaultTypes], { 
                        refetchActive: true,
                        refetchInactive: true 
                    });
                }
            } catch (error) {
                console.error('Failed to process deletion:', error);
                // Continue with other changes even if one deletion fails
            }
        }
        
        // Only process non-deletion changes if there are any
        if (nonDeletionChanges.length > 0) {
            await saveMutation.mutateAsync(nonDeletionChanges);
        }
    };

    // Calculate available handling tiers
    const availableTiers = useMemo(() => {
        if (!adaptedData) return DEFAULT_WHTS;
        
        // Get all handling tier enum values
        const allTiers = DEFAULT_WHTS;
        
        // Get currently used production capabilities
        const usedTiers = new Set(
            adaptedData.productionCapabilities.map(pc => pc.name)
        );
        
        // Return tiers that are not already in use
        return allTiers.filter(tier => !usedTiers.has(tier as string));
    }, [adaptedData]);

    // Function to add a new production capability from handling tier
    const addProductionCapability = async (handlingTier: string) => {
        try {
            // Create default nodes with standard threshold values
            const changes: PendingThresholdChange[] = [{
                productionCapability: handlingTier,
                handlingTier: 'DEFAULT',
                lowThreshold: 0,
                highThreshold: 100,
                userAlias: userAlias,
                reason: 'Create new production capability',
                action: 'Add',
                isDefault: true
            }];
            
            // Save the changes
            await saveThresholds(changes);
            
            return true;
        } catch (err) {
            return false;
        }
    };

    // Function to add a new production capability with custom name
    const addCustomPC = async (pcName: string) => {
        try {
            // Add a custom production capability
            
            // Create default nodes with standard threshold values
            const changes: PendingThresholdChange[] = [{
                productionCapability: pcName,
                handlingTier: 'DEFAULT',
                lowThreshold: 0,
                highThreshold: 100,
                userAlias: userAlias,
                reason: 'Create custom production capability',
                action: 'Add',
                isDefault: true
            }];
            
            // Save the changes
            await saveThresholds(changes);
            
            // Successfully added the production capability
            
            // Force full refresh of data after adding a PC
            // This ensures we have clean state especially for Default_FC_PC
            await queryClient.invalidateQueries(['r2f-thresholds-warehouse', effectiveWarehouseId, preserveDefaultTypes], {
                refetchActive: true,
                inactive: false
            });
            await queryClient.invalidateQueries(['r2f-thresholds-warehouse', 'DEFAULT', preserveDefaultTypes], {
                refetchActive: true,
                inactive: false
            });
            
            return true;
        } catch (err) {
            console.error('Failed to add custom PC:', err);
            return false;
        }
    };

    // Mutation for deleting thresholds
    const deleteMutation = useMutation<void, Error, DeleteR2FThresholdsRequest>(
        async (request: DeleteR2FThresholdsRequest) => {
            // Call API
            const result = await deleteR2FThresholds(request);
            if (!result) {
                throw new Error('Failed to delete R2F threshold');
            }
            return;
        },
        {
            onSuccess: () => {
                // Invalidate both FC and DEFAULT queries to refresh data from both sources
                queryClient.invalidateQueries(['r2f-thresholds-warehouse', effectiveWarehouseId, preserveDefaultTypes]);
                queryClient.invalidateQueries(['r2f-thresholds-warehouse', 'DEFAULT', preserveDefaultTypes]);
            }
        }
    );

    // Delete individual handling tier (node)
    const deleteHandlingTier = async (
        productionCapability: string,
        warehouseHandlingTier: string, 
        reason: string,
        actionSource: string = 'Newton Dashboard'
    ) => {
        try {
            const request: DeleteR2FThresholdsRequest = {
                warehouseId: effectiveWarehouseId,
                productionCapability,
                warehouseHandlingTier,
                userAlias,
                actionSource,
                reason
            };
            
            await deleteMutation.mutateAsync(request);
            return true;
        } catch (err) {
            return false;
        }
    };

    // Delete entire production capability
    const deleteProductionCapability = async (
        productionCapability: string, 
        reason: string,
        actionSource: string = 'Newton Dashboard'
    ) => {
        try {
            const request: DeleteR2FThresholdsRequest = {
                warehouseId: effectiveWarehouseId,
                productionCapability,
                userAlias,
                actionSource,
                reason
            };
            
            await deleteMutation.mutateAsync(request);
            return true;
        } catch (err) {
            return false;
        }
    };

    return {
        data: adaptedData,
        isLoading,
        error: error ? (error as Error).message : undefined,
        saveThresholds,
        userCanEdit,
        availableTiers,
        addProductionCapability,  // Renamed from addShipOption
        addCustomPC,
        deleteHandlingTier,
        deleteProductionCapability
    };
}

/**
 * Adapts the API response to the component data format
 */
function adaptApiResponse(response: { warehouseId: string; r2fThreshold: HandlingTierByPC }, warehouseId: string): R2FThresholdData {
    const productionCapabilities: R2FProductionCapabilityData[] = [];
    
    // Transform the API response structure to the component structure
    Object.entries(response.r2fThreshold || {}).forEach(([pcName, nodeMap]) => {
        const pcId = `pc_${pcName.toLowerCase().replace(/\s+/g, '_')}`;
        const nodes: R2FNodeData[] = [];
        let defaultNode: R2FNodeData | null = null;
        
        // Determine if this is a network default or FC default
        const isNetworkDefault = pcName === 'Default_Network_PC';
        const isFCDefault = pcName === 'Default_FC_PC';
        const defaultType = isNetworkDefault ? 'Network' : (isFCDefault ? 'FC' : undefined);
        
        // First check if there is a DEFAULT node in the data
        const defaultNodeExists = Object.keys(nodeMap).some(nodeId => nodeId.toUpperCase() === 'DEFAULT');
        
        // Process each node in this production capability
        Object.entries(nodeMap).forEach(([nodeId, threshold]) => {
            const isDefault = nodeId.toUpperCase() === 'DEFAULT';
            const node: R2FNodeData = {
                id: `${pcId}_${nodeId.toLowerCase().replace(/\s+/g, '_')}`,
                handlingTier: nodeId,
                lowThreshold: threshold.lowThreshold,
                highThreshold: threshold.highThreshold,
                publishDateEpochMillis: threshold.publishDateEpochMillis,
                userAlias: threshold.userAlias,
                actionSource: threshold.actionSource,
                reason: threshold.reason,
                isDefault,
                defaultType: isDefault ? defaultType : undefined
            };
            
            if (isDefault) {
                defaultNode = node;
                // Skip adding default node to additionalNodes
            } else {
                nodes.push(node);
            }
        });
        
        // Do not create a synthetic default node if none exists
        // This allows PCs to exist without a DEFAULT tier
        
        // Add this production capability to the list
        productionCapabilities.push({
            id: pcId,
            name: pcName,
            defaultNode: defaultNode || undefined, // Only include if it exists
            additionalNodes: nodes,
            isNetworkDefault,
            isFCDefault,
            hasDefaultNode: !!defaultNode // Explicitly track whether it has a default node
        });
    });
    
    return {
        warehouseId,
        productionCapabilities
    };
}

/**
 * Prepares the threshold publish request from the pending changes,
 * returning only the non-deletion changes for the publish API
 */
function prepareThresholdPublishRequest(
    changes: PendingThresholdChange[],
    warehouseId: string,
    userAlias: string
): R2FToPublish[] {
    // Filter out deletion changes (those will be handled separately)
    const nonDeletionChanges = changes.filter(change => change.action !== 'Delete');
    
    // Group changes by production capability
    const changesByPC: Record<string, PendingThresholdChange[]> = {};
    
    // Process non-deletion changes
    nonDeletionChanges.forEach(change => {
        const pc = change.productionCapability;
        if (!changesByPC[pc]) {
            changesByPC[pc] = [];
        }
        changesByPC[pc].push(change);
    });
    
    // Create the publish request
    const thresholdsToPublish: HandlingTierByPC = {};
    
    // Process each production capability
    Object.entries(changesByPC).forEach(([pc, pcChanges]) => {
        if (!thresholdsToPublish[pc]) {
            thresholdsToPublish[pc] = {};
        }
        
        // Process each change for this production capability
        pcChanges.forEach(change => {
            // Add or modify
            const threshold: PublishedR2FThresholds = {
                lowThreshold: change.lowThreshold,
                highThreshold: change.highThreshold,
                userAlias: change.userAlias || userAlias,
                reason: change.reason || 'UI Update',
                publishDateEpochMillis: Date.now(),
                actionSource: change.actionSource || 'Newton Dashboard'
            };
            
            thresholdsToPublish[pc][change.handlingTier] = threshold;
        });
    });
    
    // Create the final request for non-deletion changes
    return [{
        warehouseId,
        r2fToPublish: thresholdsToPublish
    }];
}
